import { Link } from '@reach/router'
import React, { Component } from 'react'
import Logo from '../images/logo_horizontal.js'

import { getClosest } from '../utilities'

import SolutionsDropdown from './SolutionsDropdown'
import ViewDemo from './ViewDemo'
import ResourcesDropdown from './ResourcesDropdown'
import ViewVideo from './viewVideo'

export default class Header extends Component {
    state = {
        showSolutionsMenu: false,
        showMobileMenu: false,
        showResourcesMenu: false,
    }

    constructor(props) {
        super(props)

        // Need to bind the function definition so we can remove the event listener when un-mounting this component (like when changing routes)
        this.headerMouseDown = this.headerMouseDown.bind(this)
    }

    /**
     * Toggle resources menu
     */
    toggleResources = () => {
        this.setState((oldState) => ({
            showResourcesMenu: !oldState.showResourcesMenu,
        }))

        this.hideSolutions()
    }

    /**
     * Toggle solutions menu
     */
    toggleSolutions = () => {
        this.setState((oldState) => ({
            showSolutionsMenu: !oldState.showSolutionsMenu,
        }))

        this.hideResources()
    }

    /**
     * Hide solutions menu
     */
    hideSolutions = () => {
        this.setState({ showSolutionsMenu: false })
    }

    /**
     * Hide resources menu
     */
    hideResources = () => {
        this.setState({ showResourcesMenu: false })
    }

    /**
     * Toggle mobile menu
     */
    toggleMobileMenu = () => {
        this.setState({ showMobileMenu: true })
    }

    /**
     * Hide mobile menu
     */
    hideMobileMenu = () => {
        this.setState({ showMobileMenu: false })
    }

    /**
     * Handle events when someone clicks outside of an open menu
     * @param e
     */
    headerMouseDown(e) {
        if (this.state.showResourcesMenu) {
            this.handleClick(e, this.toggleResources)
        }

        if (this.state.showSolutionsMenu) {
            this.handleClick(e, this.hideSolutions)
        }

        if (this.state.showMobileMenu) {
            this.handleClick(e, this.hideMobileMenu)
        }
    }

    /**
     * Add event listeners when the comp is mounted
     */
    componentDidMount = () => {
        document.addEventListener('mousedown', this.headerMouseDown)
    }

    /**
     * Clean up event listeners to prevent duplicate events
     */
    componentWillUnmount = () => {
        document.removeEventListener('mousedown', this.headerMouseDown)
    }

    /**
     * Abstract function for handling clicks outside of menus
     * @param e event
     * @param x function to get executed if not a direct ancestor of excludeAncestorsClassName
     * @param excludeAncestorsClassName class
     */
    handleClick = (
        e,
        x,
        excludeAncestorsClassName = '.dropdown-menu--js-target'
    ) => {
        const isInDropdown = getClosest(e.target, excludeAncestorsClassName)
        if (isInDropdown) {
            return
        }

        x()
    }

    isActive = ({ isCurrent }) => {
        return isCurrent ? { className: 'active' } : null
    }

    // If the header is being used in the launchpad, we want to treat all the links
    // as 'external' links so we don't use any browser history features.
    getLink = (to, text, linkArgs = {}) => {
        const { isLaunchpad } = this.props

        return isLaunchpad ? (
            <a href={to}>{text}</a>
        ) : (
            <Link {...linkArgs} getProps={this.isActive} to={to}>
                {text}
            </Link>
        )
    }

    render() {
        const { isLaunchpad } = this.props
        return (
            <React.Fragment>
                <div className="header sticky">
                    <div className="hs-logo-powered-by">
                        {this.getLink('/', <Logo />, {
                            className: 'home-link',
                        })}
                        <span className="poweredBy">
                            <a href="https://lwolf.com">Powered by Lone Wolf</a>
                        </span>
                    </div>
                    <MobileMenu
                        toggleMobileMenu={this.toggleMobileMenu}
                        showMobileMenu={this.state.showMobileMenu}
                        isActive={this.isActive}
                        getLink={this.getLink}
                        isLaunchpad={isLaunchpad}
                    />
                    <ul className="header-nav-links">
                        <li className="solutions-dropdown__wrap">
                            <span
                                className={'dropdown-menu--js-target'}
                                onClick={this.toggleSolutions}
                                role="button"
                                tabIndex={0}
                            >
                                Solutions
                                <span className="caret" />
                            </span>
                            <SolutionsDropdown
                                show={this.state.showSolutionsMenu}
                                getLink={this.getLink}
                            />
                        </li>

                        <li>{this.getLink('/gethelp', 'Support')}</li>

                        <li className={'resources-dropdown__wrap'}>
                            <span
                                className={'dropdown-menu--js-target'}
                                onClick={this.toggleResources}
                                role="button"
                                tabIndex={0}
                            >
                                Popular resources
                                <span className="caret" />
                            </span>

                            <ResourcesDropdown
                                show={this.state.showResourcesMenu}
                                getLink={this.getLink}
                            />
                        </li>
                    </ul>
                    <ul className="header-nav-links" id="header-cta-buttons">
                        {!isLaunchpad && (
                            <ViewDemo
                                targetTag="li"
                                targetClassName="button button-cta __cta_tag"
                            >
                                Request Demo
                            </ViewDemo>
                        )}
                        {isLaunchpad && (
                            <ViewVideo
                                url={
                                    'https://homespotter.wistia.com/medias/mk6dkhcnk9'
                                }
                            >
                                <div
                                    className="button button-cta __cta_tag"
                                    style={{ marginRight: '19px' }}
                                >
                                    View Demo
                                </div>
                            </ViewVideo>
                        )}
                        <li className="sign-in-button button button-secondary">
                            <a
                                href={
                                    isLaunchpad
                                        ? 'https://homespotter.com/signin'
                                        : '/signin'
                                }
                            >
                                Sign In
                            </a>
                        </li>
                    </ul>
                </div>
            </React.Fragment>
        )
    }
}

const MobileMenu = ({
    showMobileMenu,
    toggleMobileMenu,
    isActive,
    getLink,
    isLaunchpad,
}) => {
    let mobileNav = (
        <ul className="mobileList dropdown-menu--js-target">
            <li>{getLink('/why-homespotter', 'Why HomeSpotter?')}</li>
            <li>{getLink('/gethelp', 'Support')}</li>
            <li>
                <a href="http://blog.homespotter.com/">Blog</a>
            </li>
            <li>{getLink('/careers', 'Careers')}</li>
            <li>
                <ul className="mobile-cta-buttons">
                    {!isLaunchpad && (
                        <ViewDemo
                            targetTag="li"
                            targetClassName="button button-cta __cta_tag"
                        >
                            Request Demo
                        </ViewDemo>
                    )}
                    <li className="sign-in-button button button-secondary">
                        <a
                            href={
                                isLaunchpad
                                    ? 'https://homespotter.com/signin'
                                    : '/signin'
                            }
                        >
                            Sign In
                        </a>
                    </li>
                </ul>
            </li>
        </ul>
    )

    return (
        <ul className="mobile-nav-links">
            {showMobileMenu ? (
                <React.Fragment>
                    <div className="modal-exit">
                        <span
                            onClick={toggleMobileMenu}
                            role="button"
                            tabIndex={0}
                            className="exit"
                        >
                            &times;
                        </span>
                    </div>
                    <div className="mobile-menu">
                        <SolutionsDropdown
                            show={showMobileMenu}
                            mobile={mobileNav}
                            getLink={getLink}
                        />
                    </div>
                </React.Fragment>
            ) : (
                <li onClick={toggleMobileMenu} className="mobile-nav">
                    Menu
                </li>
            )}
        </ul>
    )
}
